import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Target,
  TrendingUp,
  Trash2,
  CheckCircle2,
  BarChart3,
  Users,
  Gamepad2,
  Layers
} from 'lucide-react';

const OurStory = () => {
  return (
    <section className="py-20 md:py-32 grid-bg relative" data-testid="our-story-section">
      <div className="hero-glow -top-40 -right-40 opacity-30" />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <h1 className="font-heading text-5xl md:text-6xl font-bold text-white mb-8">
              Our Story
            </h1>
            <p className="text-xl text-neon-green font-medium mb-6">
              The ROAS Labs was founded with a simple belief:
            </p>
            <p className="text-2xl text-white font-heading mb-8">
              Performance marketing should drive measurable business outcomes — not impressions.
            </p>
            <p className="text-gray-400 text-lg leading-relaxed mb-6">
              Built by growth strategists with deep experience in app ecosystems, gaming monetization, and digital media, we combine:
            </p>
            <ul className="space-y-3 mb-8">
              {['Media science', 'Creative psychology', 'Data engineering', 'Revenue strategy'].map((item, index) => (
                <li key={index} className="flex items-center gap-3 text-gray-300">
                  <CheckCircle2 className="w-5 h-5 text-neon-green flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <p className="text-neon-cyan text-lg font-medium">
              We act as an extension of your growth team.
            </p>
          </div>
          
          <div className="relative">
            <div className="glass rounded-2xl overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1725273549300-cda8de33868c?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" 
                alt="Team working in dark mode environment"
                className="w-full h-[400px] object-cover opacity-80"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-surface via-transparent to-transparent" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Mission = () => {
  return (
    <section className="py-20 md:py-32 bg-surface" data-testid="mission-section">
      <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
        <h2 className="font-heading text-4xl md:text-5xl font-semibold text-white mb-8">
          Our Mission
        </h2>
        <p className="text-xl md:text-2xl text-gray-300 leading-relaxed">
          To become the most trusted performance marketing partner for <span className="text-neon-green">mobile-first brands</span> across emerging and high-growth markets.
        </p>
      </div>
    </section>
  );
};

const Philosophy = () => {
  const philosophies = [
    {
      icon: Target,
      title: 'Measure What Matters',
      description: 'Revenue, LTV, Retention, ROAS.',
      color: 'bg-neon-green/10 border-neon-green/30'
    },
    {
      icon: TrendingUp,
      title: 'Scale What Works',
      description: 'Structured testing → Data validation → Budget acceleration.',
      color: 'bg-neon-cyan/10 border-neon-cyan/30'
    },
    {
      icon: Trash2,
      title: "Kill What Doesn't",
      description: 'Fast iteration. No emotional attachment to campaigns.',
      color: 'bg-neon-purple/10 border-neon-purple/30'
    },
  ];

  return (
    <section className="py-20 md:py-32 grid-bg" data-testid="philosophy-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <h2 className="font-heading text-4xl md:text-5xl font-semibold text-white text-center mb-16">
          Our Philosophy
        </h2>
        
        <div className="grid md:grid-cols-3 gap-8">
          {philosophies.map((item, index) => (
            <div 
              key={index}
              className={`glass rounded-xl p-8 border ${item.color} card-hover`}
              data-testid={`philosophy-card-${index}`}
            >
              <div className="w-14 h-14 rounded-lg bg-white/5 flex items-center justify-center mb-6">
                <item.icon className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-heading text-2xl font-semibold text-white mb-4">{item.title}</h3>
              <p className="text-gray-400 text-lg leading-relaxed">{item.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const WhyChooseUs = () => {
  const reasons = [
    { icon: TrendingUp, text: 'ROI-focused execution' },
    { icon: BarChart3, text: 'Transparent reporting dashboards' },
    { icon: Target, text: 'Weekly growth diagnostics' },
    { icon: Gamepad2, text: 'Deep experience in gaming & mobile UA' },
    { icon: Layers, text: 'Scalable paid acquisition frameworks' },
  ];

  return (
    <section className="py-20 md:py-32 bg-surface" data-testid="why-choose-us-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-white mb-8">
              Why Advertisers Choose Us
            </h2>
            <p className="text-gray-400 text-lg mb-8">
              We deliver performance, not promises. Our clients trust us because we focus on what matters most: their bottom line.
            </p>
            <Link to="/contact" className="btn-primary inline-flex items-center gap-2" data-testid="why-choose-cta">
              Start Your Growth Journey
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          
          <div className="space-y-4">
            {reasons.map((reason, index) => (
              <div 
                key={index}
                className="glass rounded-xl p-6 flex items-center gap-4 card-hover"
                data-testid={`reason-card-${index}`}
              >
                <div className="w-12 h-12 rounded-lg bg-neon-green/10 flex items-center justify-center flex-shrink-0">
                  <reason.icon className="w-6 h-6 text-neon-green" />
                </div>
                <p className="text-white font-medium text-lg">{reason.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const About = () => {
  return (
    <div data-testid="about-page">
      <OurStory />
      <Mission />
      <Philosophy />
      <WhyChooseUs />
    </div>
  );
};

export default About;
